import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Link,
} from "@mui/material";
import SearchedHash from "./components/SearchedHash";
import api from "./api"; // Import the API with configured HashSearchService
import { getNotaryVerificationPath } from "./config";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { createTheme } from "@mui/material/styles";
import { getNetwork, getExplorerUrls } from "./config";
import Logo from "./Logo";

const HashSearch = () => {
  const theme = createTheme();
  const [searchParams] = useSearchParams();
  const [hash, setHash] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [discloseData, setDiscloseData] = useState(null);
  const [showNotary, setShowNotary] = useState(false);
  const [searchProgress, setSearchProgress] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const network = getNetwork();
  const iframeRef = useRef(null);
  const hashBlock = false

  const { darkMode } = useTheme();

  const getExplorerUrl = (type, hash, network) => {
    if (!hash) return null;

    try {
      const baseUrl = getExplorerUrls()[type][network];
      return `${baseUrl}/${hash}`;
    } catch (error) {
      return null;
    }
  };
  //test
  const handleSearch = useCallback(
    async (searchHash) => {
      setLoading(true);
      setError(null);
      setResult(null);
      setDiscloseData(null);
      setShowNotary(false);
      setSearchProgress(null);

      try {
        // Get all URL parameters
        const timestamp = searchParams.get("timestamp");
        const disclose = searchParams.get("disclose");

        // Start the search using the API
        const initialData = await api.search(searchHash, timestamp);

        if (initialData.success && initialData.searchId) {
          // Set up progress updates
          const cancelUpdates = api.getSearchProgress(
            initialData.searchId,
            (update) => {
              switch (update.type) {
                case "search_started":
                  setSearchProgress({
                    message: update.message,
                    totalChunks: update.totalChunks,
                    currentChunk: 0,
                    percentComplete: 0,
                  });
                  break;

                case "processing_chunks":
                  setSearchProgress({
                    message: update.message,
                    currentBatch: update.currentBatch,
                    totalBatches: update.totalBatches,
                    percentComplete: parseFloat(update.percentComplete),
                  });
                  break;

                case "progress":
                  setSearchProgress({
                    message: "Searching and verifying block",
                    currentBlock: update.currentBlock,
                    blocksProcessed: update.blocksProcessed,
                    totalBlocks: update.totalBlocks,
                    percentComplete: parseFloat(update.percentComplete),
                  });
                  break;

                case "hash_found":
                  setSearchProgress({
                    message: update.message,
                    blockNumber: update.blockNumber,
                    foundInInput: update.foundInInput,
                    percentComplete: 100,
                  });
                  break;

                case "complete":
                  setResult(update.data);
                  cancelUpdates();
                  setLoading(false);
                  setSearchProgress(null);

                  // Use the disclose parameter from the URL if it exists
                  // Otherwise fall back to the search hash
                  const discloseValue = disclose || searchHash;
                  setDiscloseData(discloseValue);
                  setShowNotary(true);
                  break;

                case "error":
                  setError(update.message || "An error occurred during search");
                  cancelUpdates();
                  setLoading(false);
                  setSearchProgress(null);
                  break;

                default:
                  break;
              }
            }
          );
        } else {
          setError("Invalid response when initiating search");
          setLoading(false);
        }
      } catch (err) {
        console.error("Search error:", err);
        setError(
          err.message || "An error occurred while initiating the search"
        );
        setLoading(false);
      }
    },
    [searchParams]
  );

  useEffect(() => {
    const hashParam = searchParams.get("hash");
    if (hashParam) {
      setHash(hashParam);
      handleSearch(hashParam);
    }
  }, [searchParams, handleSearch]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Function to inject CSS into iframe
  const injectIframeCSS = () => {
    if (iframeRef.current) {
      try {
        const iframe = iframeRef.current;
        const iframeDocument =
          iframe.contentDocument || iframe.contentWindow.document;

        // Create a style element
        const style = iframeDocument.createElement("style");
        style.textContent = `
          /* Hide the Paystand seal and header section. Also hide the type-data section */
          .assurety-intro, 
          .badge,
          h1:first-of-type,
          .type-data {
            display: none !important;
          }
          
          /* Minimal padding for content */
          .content {
            padding: 15px 20px !important;
            box-shadow: none !important;
          }
          
          /* Remove padding for wrapper */
          .wrapper {
            padding: 0 !important;
            background: #FFFFFF !important;
            box-shadow: none !important;
          }
          
          /* Asegurar que ningún elemento dentro del iframe tenga box-shadow */
          *, div, table, td, th, [class*="assur"], [class*="verify"], [id*="assur"], [id*="verify"] {
            box-shadow: none !important;
          }
          
          /* Dark mode styling - only apply when darkMode is true */
          ${
            darkMode
              ? `
          /* Base styles for dark mode */
          html, body, div, p, span, td, th, h1, h2, h3, h4, h5, h6 {
            color: #e2e8f0 !important;
          }
          
          /* Backgrounds */
          html, body, .wrapper, .content, div, .container, td, th, tr, table, 
          [class*="assur"], [class*="verify"], [id*="assur"], [id*="verify"] {
            background-color: #1e1e1e !important;
          }
          
          /* Reset all borders to dark */
          *, div, td, th, table, [style*="border"] {
            border-color: rgba(51, 51, 55, 0.8) !important;
          }
          
          /* Style all three verification rows */
          [class*="imprint"], [class*="calculation"], [class*="blockchain"],
          [id*="imprint"], [id*="calculation"], [id*="blockchain"],
          table tr, table td {
            background-color: #1e1e1e !important;
            color: #e2e8f0 !important;
            border: 1px solid rgba(51, 51, 55, 0.8) !important;
          }
          
          /* Style verification status icons to match our UI */
          .material-icons.check, svg.check, [class*="success"] svg, .material-icons.done,
          svg[class*="check"], svg[data-icon="check"], [class*="success"] i {
            color: #4ade80 !important;
            fill: #4ade80 !important;
          }
          
          /* Style refresh/loading icons - more specific and stronger rules */
          [class*="refresh"], [class*="sync"], [class*="loading"], [class*="progress"], 
          [class*="spinner"], [class*="rotating"], [class*="circle"],
          svg[class*="refresh"], svg[class*="sync"], svg[class*="loading"],
          .material-icons.refresh, .material-icons.sync, .material-icons.loop, 
          .material-icons.cached, .material-icons.autorenew, svg[class*="loading"] {
            display: inline-block !important;
            visibility: visible !important;
            opacity: 1 !important;
            color: #60a5fa !important;
            fill: #60a5fa !important;
            width: initial !important;
            height: initial !important;
            position: relative !important;
            overflow: visible !important;
            animation-play-state: running !important;
          }

          /* Additional specific overrides */
          .content-section, .assurety-content, .wrapper > div,
          [class*="content"], [class*="section"], [class*="container"] {
            background-color: #1e1e1e !important;
          }
          
          /* Hide Assured Data section but keep it in DOM */
          div[style*="margin: 30px 0"] h4 a {
            visibility: hidden !important;
            position: relative !important;
          }
          
          /* Add replacement text */
          div[style*="margin: 30px 0"] h4 a:after {
            content: '' !important;
            visibility: hidden !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
          }
          
          /* Hide all blockchain info at the bottom */
          div[style*="margin-top: 30px"] {
            display: none !important;
          }
          
          /* Hide the footer */
          .footer {
            display: none !important;
          }
          `
              : ""
          }
        `;

        // Append the style to the head
        iframeDocument.head.appendChild(style);

        // Basic function to style check and refresh icons directly
        const styleVerificationIcons = () => {
          if (darkMode) {
            // Style refresh/loading icons with more variations
            const refreshIcons = iframeDocument.querySelectorAll(
              '[class*="refresh"], [class*="sync"], [class*="loading"], [class*="spinner"], ' +
                ".material-icons.refresh, .material-icons.sync, .material-icons.loop, " +
                '.material-icons.cached, .material-icons.autorenew, svg[class*="loading"]'
            );
            refreshIcons.forEach((icon) => {
              // Ensure refresh icons are fully visible
              icon.style.display = "inline-block";
              icon.style.visibility = "visible";
              icon.style.opacity = "1";
              icon.style.color = "#60a5fa";
              icon.style.fill = "#60a5fa";
              icon.style.width = "initial";
              icon.style.height = "initial";
              icon.style.position = "relative";
              icon.style.overflow = "visible";

              // Preserve any animation
              if (icon.style.animation) {
                icon.style.animationPlayState = "running";
              }
            });

            // Style check/success icons
            const checkIcons = iframeDocument.querySelectorAll(
              ".material-icons.check, .material-icons.done, svg.check"
            );
            checkIcons.forEach((icon) => {
              icon.style.color = "#4ade80";
              icon.style.fill = "#4ade80";
            });
          }
        };

        // Run immediately
        styleVerificationIcons();

        // Set up a simple MutationObserver for styling
        const observer = new MutationObserver(() => {
          styleVerificationIcons();
        });

        // Start observing
        observer.observe(iframeDocument.body, {
          childList: true,
          subtree: true,
        });

        // Wait for a bit to make sure everything is loaded
        setTimeout(() => {
          try {
            if (darkMode) {
              // Apply inline styles to critical elements
              const mainElements = [
                iframeDocument.documentElement,
                iframeDocument.body,
                ...iframeDocument.querySelectorAll(
                  '.wrapper, .content, [class*="container"]'
                ),
              ];

              mainElements.forEach((el) => {
                if (el) {
                  el.style.backgroundColor = "#1e1e1e";
                  el.style.color = "#e2e8f0";
                }
              });

              // Target specific verification rows
              const rows = iframeDocument.querySelectorAll(
                '[class*="imprint"], [class*="blockchain"], div[style*="border"]'
              );
              rows.forEach((row) => {
                row.style.backgroundColor = "#1e1e1e";
                row.style.color = "#e2e8f0";
                row.style.border = "1px solid rgba(51, 51, 55, 0.8)";

                // Fix any children
                const children = row.querySelectorAll("*");
                children.forEach((child) => {
                  child.style.backgroundColor = "#1e1e1e";
                });
              });

              // Style verification icons again
              styleVerificationIcons();

              // Force dark background on any remaining white elements
              const allDivs = iframeDocument.querySelectorAll("div");
              allDivs.forEach((div) => {
                const style = window.getComputedStyle(div);
                if (
                  style.backgroundColor === "rgb(255, 255, 255)" ||
                  style.backgroundColor === "#ffffff"
                ) {
                  div.style.backgroundColor = "#1e1e1e";
                }
              });
            }
          } catch (err) {
            console.error("Error in iframe DOM manipulation:", err);
          }
        }, 800);
      } catch (err) {
        console.error("Error injecting CSS into iframe:", err);
      }
    }
  };

  useEffect(() => {
    // If the iframe is shown and the iframe element exists
    if (showNotary && iframeRef.current) {
      // Try to inject CSS when iframe loads
      const iframe = iframeRef.current;
      iframe.onload = injectIframeCSS;

      // Try multiple times to ensure it applies after any dynamic content loads
      setTimeout(injectIframeCSS, 500);
      setTimeout(injectIframeCSS, 1000);
      setTimeout(injectIframeCSS, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotary, darkMode]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={1.5}
        p={2}
        sx={{
          background: darkMode
            ? "linear-gradient(to bottom, #111827, #1f2937)"
            : "#f1f3f7",
          position: "relative",
        }}
      >
        {/* Título principal "Paystand Assurety" */}
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontWeight: 700,
            color: darkMode ? "#ffffff" : "#182d55",
            mb: 1,
            mt: "1rem",
            letterSpacing: 1,
            position: "relative",
            zIndex: 1,
            textShadow: darkMode
              ? "0 0 15px rgba(255, 255, 255, 0.6)"
              : "0 0 15px rgba(24, 45, 85, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            maxWidth: 1000,
            width: "100%",
            mx: "auto",
            px: { xs: 2, sm: 3 },
            fontSize: "30px",
          }}
        >
          <Logo style={{ width: "90px", height: "100px" }} />
          
          Paystand Assurety
        </Typography>

        <Box
          width="100%"
          maxWidth={600}
          sx={{
            backgroundColor: darkMode
              ? "rgba(30, 30, 30, 0.85)"
              : "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(10px)",
            borderRadius: 2,
            border: `1px solid ${
              darkMode ? "rgba(255, 165, 0, 0.2)" : "rgba(0, 0, 0, 0.05)"
            }`,
            boxShadow: "none",
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            position: "relative",
            zIndex: 1,
          }}
        >
          {searchProgress ? (
            <>
              <Typography
                variant="h5"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: "#182d55",
                  mb: 1,
                }}
              >
                {searchProgress.percentComplete >= 99
                  ? "Finalizing Results..."
                  : searchProgress.message || "Verifying on Blockchain..."}
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{
                  color: darkMode ? "#e2e8f0" : "#1e293b",
                  mb: 2,
                }}
              >
                {searchProgress.percentComplete >= 99
                  ? "We are about to show your results"
                  : "Searching for your hash in the Bitcoin blockchain"}
              </Typography>

              {/* Progress Bar styled as a blockchain */}
              <Box
                sx={{
                  width: "100%",
                  mb: 1,
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircularProgress
                  variant="indeterminate"
                  size={80}
                  thickness={4}
                  sx={{
                    color: "#182d55",
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: "#182d55",
                  mb: 1,
                }}
              >
                Initializing Bitcoin transaction search
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{
                  color: darkMode ? "#e2e8f0" : "#1e293b",
                  mb: 2,
                }}
              >
                Bitcoin blockchain verification in progress
              </Typography>
            </>
          )}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={1.5}
        p={2}
        sx={{
          background: darkMode
            ? "linear-gradient(to bottom, #111827, #1f2937)"
            : "#f1f3f7",
          position: "relative",
        }}
      >
        {/* Título principal "Paystand Assurety" */}
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontWeight: 700,
            color: darkMode ? "#ffffff" : "#182d55",
            mb: 1,
            mt: "1rem",
            letterSpacing: 1,
            position: "relative",
            zIndex: 1,
            textShadow: darkMode
              ? "0 0 15px rgba(255, 255, 255, 0.6)"
              : "0 0 15px rgba(24, 45, 85, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            maxWidth: 1000,
            width: "100%",
            mx: "auto",
            px: { xs: 2, sm: 3 },
            fontSize: "30px",
          }}
        >
         <Logo style={{ width: "90px", height: "100px" }} />
          Paystand Assurety
        </Typography>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 600,
            width: "100%",
            backgroundColor: darkMode ? "rgba(30, 30, 30, 0.75)" : "#FFFFFF",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            padding: 3,
            border: "1px solid rgba(239, 68, 68, 0.3)",
            boxShadow: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
 <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: darkMode ? "white" : "#182d55",
              fontWeight: 700,
              textAlign: "center",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1
            }}
          >
            Error
          </Typography>

          <Typography
            sx={{
              color: "#ef4444",
              textAlign: "center",
              mb: 3,
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            
            {error}
          </Typography>

          <Box
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            alignItems="center"
            gap={2}
            sx={{
              width: "100%",
              mt: 2,
              [theme.breakpoints.up("md")]: {
                flexDirection: "row",
                mt: 4,
              },
            }}
          >
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                backgroundColor: "#00aef0",
                "&:hover": {
                  backgroundColor: "#0099d0",
                },
                boxShadow: "none",
                px: 3,
                width: "100%",
                [theme.breakpoints.up("md")]: {
                  width: "auto",
                },
              }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={() => (window.location.href = "/")}
              sx={{
                borderColor: "#182d55",
                color: "#182d55",
                "&:hover": {
                  borderColor: "#253e75",
                  backgroundColor: "rgba(24, 45, 85, 0.04)",
                },
                boxShadow: "none",
                px: 3,
                width: "100%",
                [theme.breakpoints.up("md")]: {
                  width: "auto",
                },
              }}
            >
              Search Another Hash
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!result) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={1.5}
        p={2}
        sx={{
          background: darkMode
            ? "linear-gradient(to bottom, #111827, #1f2937)"
            : "#f1f3f7",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 600,
            width: "100%",
            backgroundColor: darkMode ? "rgba(30, 30, 30, 0.75)" : "#FFFFFF",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            padding: 3,
            border: "1px solid rgba(0, 0, 0, 0.05)",
            boxShadow: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ef4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: darkMode ? "white" : "#182d55",
              fontWeight: 700,
              textAlign: "center",
              mb: 2,
            }}
          >
            No Results Found
          </Typography>

          <Typography
            sx={{
              color: "#ef4444",
              textAlign: "center",
              mb: 3,
              fontWeight: 500,
            }}
          >
            No hash match was found in the Bitcoin blockchain
          </Typography>

          <Box
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            alignItems="center"
            gap={2}
            sx={{
              width: "100%",
              mt: 2,
              [theme.breakpoints.up("md")]: {
                flexDirection: "row",
                mt: 4,
              },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => (window.location.href = "/")}
              sx={{
                borderColor: "#182d55",
                color: "#182d55",
                "&:hover": {
                  borderColor: "#253e75",
                  backgroundColor: "rgba(24, 45, 85, 0.04)",
                },
                boxShadow: "none",
                px: 3,
                width: "100%",
                [theme.breakpoints.up("md")]: {
                  width: "auto",
                },
              }}
            >
              Search Another Hash
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      minHeight="100vh"
      sx={{
        background: darkMode
          ? "linear-gradient(to bottom, #111827, #1f2937)"
          : "#f1f3f7",
        position: "relative",
        pt: 3,
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1000,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          mb: 2
        }}
      >
        <div style={{ 
          display: "flex", 
          alignItems: "flex-start", 
          gap: 4
        }}>
        {/* Logo a la izquierda */}
        <Logo style={{ 
          width: "120px", 
          height: "130px", 
          flexShrink: 0
        }} />

        <div style={{ display: "flex", flexDirection: "column", paddingLeft: "1rem",paddingTop: "1.1rem" }}>
          {/* Título principal "Paystand Assurety" */}
          <Typography
            variant="h3"
            align="left"
            sx={{
              fontWeight: 700,
              color: darkMode ? "#ffffff" : "#182d55",
              mb: "10px",
              letterSpacing: 1,
              position: "relative",
              zIndex: 1,
              textShadow: darkMode
                ? "0 0 15px rgba(255, 255, 255, 0.6)"
                : "0 0 15px rgba(24, 45, 85, 0.3)",
              fontSize: "30px",
            }}
          >
            Paystand Assurety
          </Typography>

          {/* Descripción de Paystand Assurety */}
          <Typography
            variant="body1"
            align="left"
            sx={{
              color: darkMode
                ? "rgba(255, 255, 255, 0.7)"
                : "rgba(24, 45, 85, 0.7)",
              fontSize: "14px"
            }}
          >
            Paystand Assurety is our secure blockchain-based system for storing and
            verifying your payment records. It ensures your data is tamper-proof and
            independently auditable.
          </Typography>
        </div>
        </div>
      </Box>
      {/* How it works subtitle */}
      <Typography
        variant="h5"
        align="left"
        sx={{
          fontWeight: 600,
          color: darkMode ? "#ffffff" : "#182d55",
          mb: 1,
          mt: "2rem",
          opacity: 0.9,
          letterSpacing: 0.5,
          position: "relative",
          zIndex: 1,
          maxWidth: 1000,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          fontSize: "20px",
        }}
      >
        How it works
      </Typography>

      {/* Descripción de How it works */}
      <Typography
        variant="body1"
        align="left"
        sx={{
          color: darkMode
            ? "rgba(255, 255, 255, 0.7)"
            : "rgba(24, 45, 85, 0.7)",
          maxWidth: 1000,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          mb: 3,
          fontSize: "14px",
        }}
      >
        We securely store your payment data in our private blockchain. At
        regular intervals, we pin a cryptographic proof of this data to the
        public blockchain. This process ensures your records are tamper-proof
        and can always be independently verified.
      </Typography>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1000,
          width: "100%",
          mx: "auto",
          pt: 0,
          px: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
        }}
      >
        {/* Bloque de éxito con verificación */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: darkMode ? "rgba(30, 30, 30, 0.75)" : "#FFFFFF",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            overflow: "hidden",
            transition: "all 0.3s ease",
            boxShadow: "none",
            mb: 3,
            border: "1px solid rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Bloque de verificación */}
          <Box
            sx={{
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Fila 1: Icono de verificación, Payment Verified y botón View on Chain */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexGrow: 1,
                }}
              >
                <Box
                  sx={{
                    color: "#69a061",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    sx={{ mr: 2 }}
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </Box>
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: darkMode ? "#ffffff" : "#182d55",
                      fontSize: "16px",
                    }}
                  >
                    Payment Verified
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: darkMode
                        ? "rgba(255, 255, 255, 0.7)"
                        : "rgba(24, 45, 85, 0.7)",
                      fontWeight: 700,
                      fontSize: "13px",
                    }}
                  >
                    Pinned on the public blockchain
                  </Typography>
                </Box>
              </Box>

              <Link
                href={`${getExplorerUrl(
                  "bitcoin",
                  result?.startingPin?.BitcoinHash,
                  network
                )} `}
                target="_blank"
                rel="noopener"
                sx={{
                  borderRadius: "50px",
                  py: 1,
                  px: 2.5,
                  color: "#ffffff",
                  background:
                    "linear-gradient(225deg, rgba(24, 45, 85, 0.9) 0%, rgba(36, 62, 110, 0.9) 100%)",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  letterSpacing: "1px",
                  textDecoration: "none",
                  border: "0.5px solid rgba(255, 255, 255, 0.1)",
                  transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "none",
                    background:
                      "linear-gradient(225deg, rgba(36, 62, 110, 0.95) 0%, rgba(24, 45, 85, 0.95) 100%)",
                    "& .btx-icon": {
                      transform: "rotate(3deg) scale(1.05)",
                      filter: "brightness(1.2)",
                    },
                  },
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  className="btx-icon"
                  style={{
                    transition: "transform 0.3s ease",
                  }}
                >
                  <circle cx="12" cy="12" r="11" fill="#f7931a" />
                  <path
                    d="M15.75 9.92c.16-1.08-.67-1.67-1.8-2.05l.36-1.46-.89-.22-.36 1.43-.73-.18.36-1.42-.89-.22-.36 1.41c-.24-.06-.48-.11-.71-.17l.01.03-1.23-.3-.24.96s.67.15.66.16c.37.09.43.32.42.51l-.42 1.68c.01 0 .04.01.07.02l-.07-.02-.58 2.35c-.05.11-.16.28-.43.21.01.01-.66-.16-.66-.16l-.45 1.03 1.16.29c.21.06.42.11.63.17l-.37 1.48.89.22.37-1.46c.25.06.5.13.74.19l-.37 1.44.89.22.37-1.47c1.55.29 2.72.17 3.21-1.22.4-1.13-.02-1.77-.84-2.2.6-.13 1.05-.52 1.17-1.32zm-2.08 2.9c-.28 1.13-2.19.52-2.8.37l.5-2c.62.15 2.6.44 2.3 1.63zm.28-2.93c-.26 1.04-1.85.51-2.37.38l.45-1.81c.51.13 2.17.37 1.92 1.43z"
                    fill="white"
                  />
                </svg>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    letterSpacing: "1px",
                    color: "#FFFFFF",
                  }}
                >
                  View on-chain
                </Typography>
              </Link>
            </Box>

            {/* Fila 2: hash abreviado con botón de copiar (ya no muestra "pinned on the public blockchain") */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: 2,
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: darkMode
                    ? "rgba(0, 0, 0, 0.2)"
                    : "rgba(0, 0, 0, 0.05)",
                  borderRadius: "50px",
                  py: 0.5,
                  px: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    color: darkMode ? "#38bdf8" : "#182d55",
                    fontWeight: 500,
                  }}
                >
                  {result?.bitcoinHash &&
                    `Tx: ${result.bitcoinHash.substring(
                      0,
                      5
                    )}...${result.bitcoinHash.substring(
                      result.bitcoinHash.length - 5
                    )}`}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(result?.bitcoinHash)}
                  sx={{
                    p: 0.3,
                    color: darkMode ? "#38bdf8" : "#182d55",
                    "&:hover": {
                      backgroundColor: darkMode
                        ? "rgba(56, 189, 248, 0.1)"
                        : "rgba(24, 45, 85, 0.1)",
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Fila 3: Pinned timestamp (sin línea divisoria) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 0.5,
                pt: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: darkMode
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(24, 45, 85, 0.7)",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                }}
              >
                Pinned:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: darkMode
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(24, 45, 85, 0.9)",
                  fontFamily: "monospace",
                  fontSize: "11px",
                }}
              >
                {result?.startingPin
                  ? new Date(result.startingPin.Timestamp).toLocaleString(
                      undefined,
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )
                  : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      waiting on pin confirmation
                    </Box>
                  )}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Botón para desplegar los detalles de verificación */}
        <Box
          onClick={() => setShowDetails(!showDetails)}
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1,
            cursor: "pointer",
            my: 2,
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "translateY(2px)",
              "& .arrow-icon": {
                transform: showDetails
                  ? "rotate(180deg) translateY(-2px)"
                  : "translateY(2px)",
              },
              "& .text": {
                color: darkMode
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(24, 45, 85, 0.9)",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              className="arrow-icon"
              sx={{
                color: darkMode ? "#38bdf8" : "#182d55",
                transition: "transform 0.3s ease",
                transform: showDetails ? "rotate(180deg)" : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </Box>
            <Typography
              className="text"
              variant="subtitle1"
              sx={{
                color: darkMode
                  ? "rgba(255, 255, 255, 0.7)"
                  : "rgba(24, 45, 85, 0.7)",
                fontWeight: 600,
                transition: "color 0.3s ease",
              }}
            >
              {showDetails
                ? "Hide verification details"
                : "Dive into the verification details"}
            </Typography>
          </Box>
        </Box>

        {/* Área desplegable con los detalles de verificación */}
        <Box
          sx={{
            display: showDetails ? "flex" : "none",
            flexDirection: "column",
            width: "100%",
            gap: 3,
            mb: 4,
            transition: "all 0.3s ease",
            backgroundColor: darkMode ? "rgba(30, 30, 30, 0.75)" : "#FFFFFF",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Contenido de los detalles */}
          <Box
            sx={{
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Ethereum Transaction Data (sin título) */}
            <Box>
              {/* Notary Verification Content */}
              {showNotary && discloseData && (
                <Box
                  sx={{
                    width: "100%",
                    position: "relative",
                    flexGrow: 1,
                    minHeight: { xs: 850, sm: 800, md: 780 },
                    mb: 3,
                    boxShadow: "none",
                  }}
                >
                  <iframe
                    ref={iframeRef}
                    src={`${
                      window.location.origin
                    }${getNotaryVerificationPath()}?t=${encodeURIComponent(
                      discloseData
                    )}&dark=${darkMode}`}
                    title="Verification"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    scrolling="no"
                    style={{
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
                      border: darkMode
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid #FFFFFF",
                      boxShadow: "none",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Transaction Details Section */}
            <Box>
              {/* Ethereum Hash Display */}
              {result?.bitcoinMatch && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="#627eea"
                      style={{ marginRight: "8px" }}
                    >
                      <path d="M11.944 17.97L4.58 13.62 11.943 24l7.37-10.38-7.372 4.35h.003zM12.056 0L4.69 12.223l7.365 4.354 7.365-4.35L12.056 0z" />
                    </svg>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: darkMode ? "#94a3b8" : "#475569",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Ethereum Hash
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: darkMode
                        ? "rgba(15, 23, 42, 0.5)"
                        : "#FFFFFF",
                      borderRadius: "0",
                      p: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: darkMode ? "#cbd5e1" : "#334155",
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        position: "relative",
                        zIndex: 1,
                        textShadow: darkMode
                          ? "0 0 1px rgba(203, 213, 225, 0.2)"
                          : "none",
                        flex: 1,
                      }}
                    >
                      {result.bitcoinMatch.ethereumHash || "N/A"}
                    </Typography>
                    <Tooltip title="Copy Ethereum Hash" arrow>
                      <IconButton
                        onClick={() =>
                          handleCopy(
                            result.bitcoinMatch.ethereumHash,
                            "ethereum"
                          )
                        }
                        size="small"
                        sx={{
                          p: 0.7,
                          backgroundColor: "rgba(24, 45, 85, 0.2)",
                          color: "#182d55",
                          "&:hover": {
                            backgroundColor: "rgba(24, 45, 85, 0.3)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {/* Searched Hash Content */}
              {hashBlock && <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#38bdf8"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginRight: "8px" }}
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: darkMode ? "#94a3b8" : "#475569",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Searched Hash
                    </Typography>
                  </Box>

                  <Chip
                    icon={
                      <CheckCircleIcon
                        sx={{
                          fontSize: "0.7rem !important",
                          color: "inherit",
                        }}
                      />
                    }
                    label="Verified"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      background:
                        "linear-gradient(90deg, rgba(52, 211, 153, 0.8), rgba(16, 185, 129, 0.7))",
                      backgroundColor: "transparent",
                      color: "#ffffff",
                      height: "20px",
                      fontSize: "0.65rem",
                      borderRadius: "10px",
                      transition: "all 0.3s ease",
                      border: "1px solid rgba(16, 185, 129, 0.2)",
                      boxShadow: "0 0 8px rgba(16, 185, 129, 0.2)",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 3px 10px rgba(16, 185, 129, 0.3)",
                      },
                      "& .MuiChip-label": {
                        px: 0.8,
                        position: "relative",
                        zIndex: 1,
                      },
                      "& .MuiChip-icon": {
                        position: "relative",
                        zIndex: 1,
                        color: "white",
                        mr: 0.3,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    backgroundColor: darkMode
                      ? "rgba(15, 23, 42, 0.5)"
                      : "#FFFFFF",
                    borderRadius: "0",
                    p: 1.5,
                  }}
                >
                  <SearchedHash
                    hash={hash}
                    blockNumber={result?.blockNumber}
                    foundInInput={result?.foundInInput}
                    onCopy={handleCopy}
                  />
                </Box>
              </Box>}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HashSearch;
